import styled, { css } from 'styled-components';
import { fontSize, fontWeight } from 'modules/theme';
import { pxToRem, unit } from 'modules/v2/foundation/spacing';
import { themeGet } from 'modules/v2/foundation/theme';
const ADORNMENT_WIDTH = 40;
export const StyledTextArea = styled.textarea `
  border: none;
  background-color: transparent;
  flex: 1 1 100%;
  font-size: ${themeGet('fontSize.medium')};
  line-height: ${themeGet('lineHeight.default')};
  color: ${themeGet('colors.text.300')};
  resize: ${(props) => props.$resize};

  ${(props) => props.$variant === 'outline' &&
    `
    box-sizing: content-box;
    padding: ${themeGet('spacing.md')} ${themeGet('spacing.lg')};
    min-height: ${`calc(${unit(props.$minHeight)} - ${themeGet('spacing.md')} * 2)`};
    max-height: ${props.$maxHeight ? `calc(${unit(props.$maxHeight)} - ${themeGet('spacing.md')} * 2)` : 'none'};
  `};

  &:focus-visible {
    outline: none;
  }

  &:disabled {
    color: ${themeGet('colors.disabled.200')};
  }

  &:not(:first-child) {
    padding-left: ${pxToRem(ADORNMENT_WIDTH)};
  }

  &:not(:last-child) {
    padding-right: ${pxToRem(ADORNMENT_WIDTH)};
  }
`;
export const Container = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${themeGet('spacing.sm')};
  position: relative;
  width: ${(props) => props.$stretch && '100%;'};
`;
export const Adornment = styled.div `
  position: absolute;
  top: 0;
  width: ${pxToRem(ADORNMENT_WIDTH)};
  height: ${pxToRem(ADORNMENT_WIDTH)};
  display: flex;
  align-items: center;
  justify-content: center;

  &:first-child {
    left: 0;
  }

  &:last-child {
    right: ${pxToRem(16)}; // This is the width of the scrollbar, this prevents overlapping
  }
`;
const descriptionInvalidStyles = css `
  color: ${themeGet('colors.text.danger')};
`;
export const Description = styled.div `
  ${fontSize('tiny')};
  ${fontWeight('normal')};
  color: ${themeGet('colors.text.300')};
  display: inline-block;
  margin-top: ${themeGet('spacing.sm')};
  line-height: ${themeGet('lineHeight.neutralized')};

  &:has(+ * textarea:disabled) {
    color: ${themeGet('colors.disabled.200')};
  }

  &:has(+ * textarea:focus-visible) {
    color: ${(props) => !props.$invalid && themeGet('colors.primary.1000')};
  }

  ${(props) => props.$invalid && descriptionInvalidStyles};
`;
const outlineInputDisabledStyles = css `
  border: ${pxToRem(1)} dashed ${themeGet('colors.disabled.200')};
  box-shadow: none;
  margin: ${pxToRem(1)}; /* prevents the input border from jumping */
`;
const outlineInputDefaultStyles = css `
  background-color: ${themeGet('colors.background.50')};
  box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.border.100')}; /* firefox fallback until :has selector is supported */

  &:has(textarea:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.border.100')};
  }

  &:hover:not(:has(textarea:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.border.100')};
  }

  &:has(textarea:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.border.300')};

    ${Adornment} svg {
      color: ${themeGet('colors.primary.1000')};
    }
  }

  &:has(textarea:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.border.300')};
  }

  &:has(textarea:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const outlineInputHighlightStyles = css `
  background-color: ${themeGet('colors.blue.50')};
  box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.blue.500')};

  &:hover:not(:has(textarea:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.blue.500')};
  }

  &:has(textarea:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.blue.500')};

    ${Adornment} svg {
      color: ${themeGet('colors.blue.500')};
    }
  }

  &:has(textarea:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.blue.500')};
  }

  &:has(textarea:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const outlineInputInvalidStyles = css `
  background-color: ${themeGet('colors.background.50')};
  box-shadow: 0 0 0 ${pxToRem(1)} ${themeGet('colors.text.danger')};

  &:hover:not(:has(textarea:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.text.danger')};
  }

  &:has(textarea:focus-visible:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.text.danger')};

    ${Adornment} svg {
      color: ${themeGet('colors.text.danger')};
    }
  }

  &:has(textarea:active:not(:disabled)) {
    box-shadow: 0 0 0 ${pxToRem(2)} ${themeGet('colors.text.danger')};
  }

  &:has(textarea:disabled) {
    ${outlineInputDisabledStyles};
  }
`;
const transparentInputStyles = css `
  border: transparent;
  outline: transparent;
`;
export const TextAreaContainer = styled.div `
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: flex-start;

  overflow: hidden;
  border-radius: ${themeGet('borderRadius.md')};
  ${(props) => props.$appearance === undefined && !props.$invalid && outlineInputDefaultStyles};
  ${(props) => props.$appearance === 'highlight' && outlineInputHighlightStyles};
  ${(props) => props.$invalid && outlineInputInvalidStyles};
  ${(props) => props.$appearance === 'transparent' && transparentInputStyles};

  margin: ${pxToRem(2)}; /* prevent box-shadow border from being hidden/covered in case of container not having any padding */
  transition: box-shadow 0.15s;
`;
